import {
  ATTACHMENT_MAX_BYTES,
  DiscardAttachmentResponseSchema,
  PreparedAttachmentSchema,
  type AttachmentKind,
  type PreparedAttachment,
} from "@herskin/contracts";
import { api } from "./api";

export const MAX_ATTACHMENTS_PER_PROMPT = 5;

const IMAGE_MIME_TYPES = new Set(["image/jpeg", "image/png", "image/webp", "image/gif"]);
const FILE_MIME_TYPES = new Set([
  "text/plain",
  "text/markdown",
  "text/csv",
  "application/json",
  "application/xml",
  "text/xml",
  "application/yaml",
  "text/yaml",
]);

const MIME_BY_EXTENSION: Record<string, string> = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  gif: "image/gif",
  pdf: "application/pdf",
  txt: "text/plain",
  md: "text/markdown",
  markdown: "text/markdown",
  csv: "text/csv",
  json: "application/json",
  xml: "application/xml",
  yaml: "application/yaml",
  yml: "application/yaml",
};

export type AttachmentChipStatus = "uploading" | "ready" | "failed" | "removing";

export interface AttachmentChip {
  clientId: string;
  sessionId: string;
  filename: string;
  mimeType: string;
  kind: AttachmentKind | null;
  sizeBytes: number;
  status: AttachmentChipStatus;
  attachmentId?: string;
  error?: string;
}

export interface AttachmentPreflight {
  kind: AttachmentKind;
  mimeType: string;
}

export interface AttachmentPreflightFailure {
  error: string;
}

function extensionOf(filename: string) {
  const dot = filename.lastIndexOf(".");
  return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : "";
}

export function preflightAttachment(
  file: Pick<File, "name" | "size" | "type">,
): AttachmentPreflight | AttachmentPreflightFailure {
  if (!file.name.trim() || file.name.length > 255) return { error: "文件名必须为 1–255 个字符" };
  if (file.size <= 0) return { error: "不能上传空文件" };

  const inferredMime = MIME_BY_EXTENSION[extensionOf(file.name)];
  const mimeType = file.type.toLowerCase() || inferredMime || "";
  let kind: AttachmentKind | null = null;
  if (IMAGE_MIME_TYPES.has(mimeType)) kind = "image";
  else if (mimeType === "application/pdf") kind = "pdf";
  else if (FILE_MIME_TYPES.has(mimeType)) kind = "file";
  if (!kind)
    return { error: "不支持此文件类型；请选择图片、PDF、文本、Markdown、CSV、JSON、XML 或 YAML" };

  const maxBytes = ATTACHMENT_MAX_BYTES[kind];
  if (file.size > maxBytes) {
    return { error: `${kind === "image" ? "图片" : "文件"}不能超过 ${formatFileSize(maxBytes)}` };
  }
  return { kind, mimeType };
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  const kibibytes = bytes / 1024;
  if (kibibytes < 1024)
    return `${kibibytes < 10 ? kibibytes.toFixed(1) : Math.round(kibibytes)} KiB`;
  const mebibytes = kibibytes / 1024;
  return `${mebibytes < 10 ? mebibytes.toFixed(1) : Math.round(mebibytes)} MiB`;
}

export function chipFromPrepared(clientId: string, prepared: PreparedAttachment): AttachmentChip {
  return {
    clientId,
    sessionId: prepared.sessionId,
    filename: prepared.filename,
    mimeType: prepared.mimeType,
    kind: prepared.kind,
    sizeBytes: prepared.sizeBytes,
    status: "ready",
    attachmentId: prepared.attachmentId,
  };
}

export function attachmentStatusLabel(attachment: AttachmentChip) {
  if (attachment.status === "uploading") return "上传中";
  if (attachment.status === "removing") return "移除中";
  if (attachment.status === "failed")
    return attachment.error ? `失败：${attachment.error}` : "失败";
  return "已暂存";
}

/**
 * 带附件的提交只要没有明确成功响应，就不能把原 attachmentId 当成可重用草稿。
 * 保留 chip 供用户理解和手动移除，但必须重新选择文件后才能发起新 command。
 */
export function lockAttachmentsAfterSubmitFailure(attachments: AttachmentChip[], reason: string) {
  return attachments.map<AttachmentChip>((attachment) => ({
    ...attachment,
    status: "failed",
    error: reason,
  }));
}

/** 文件名只进入经过 URI 编码的私有请求头，不进入 URL、日志参数或持久化草稿。 */
export async function uploadPreparedAttachment(
  file: File,
  sessionId: string,
  preflight: AttachmentPreflight,
) {
  return PreparedAttachmentSchema.parse(
    await api<unknown>("/api/attachments", {
      method: "POST",
      headers: {
        "Content-Type": "application/octet-stream",
        "x-herskin-session-id": sessionId,
        "x-herskin-attachment-kind": preflight.kind,
        "x-herskin-filename": encodeURIComponent(file.name),
        "x-herskin-mime-type": preflight.mimeType,
      },
      body: await file.arrayBuffer(),
    }),
  );
}

export async function discardPreparedAttachment(attachmentId: string, sessionId: string) {
  return DiscardAttachmentResponseSchema.parse(
    await api<unknown>(`/api/attachments/${encodeURIComponent(attachmentId)}`, {
      method: "DELETE",
      headers: { "x-herskin-session-id": sessionId },
    }),
  );
}
